/**
 * Backend DTO types matching controllers/messaging/dto/*
 * These mirror the exact shapes returned by the messaging API
 */

export enum ChannelTypeEnum {
  INTERNAL = 'internal',
  CLIENT = 'client',
  DM = 'dm',
}

export enum ChannelStatusEnum {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

export enum ChannelMemberRoleEnum {
  OWNER = 'owner',
  MEMBER = 'member',
}

export enum MessageTypeEnum {
  USER = 'user',
  SYSTEM = 'system',
}

export enum SystemEventEnum {
  MEMBER_ADDED = 'member_added',
  MEMBER_REMOVED = 'member_removed',
  MEMBER_LEFT = 'member_left',
  CHANNEL_CREATED = 'channel_created',
  CHANNEL_RENAMED = 'channel_renamed',
  CHANNEL_ARCHIVED = 'channel_archived',
  CHANNEL_UNARCHIVED = 'channel_unarchived',
}

// Base DTO types
export interface UserInfo {
  id: number;
  firstName: string;
  lastName: string;
  avatar: string;
  userType?: string;
}

export interface ProjectInfo {
  id: number;
  project: string;
}

export interface ChannelDto {
  id: number;
  name: string | null;
  type: ChannelTypeEnum;
  'project-id': number | null;
  status: ChannelStatusEnum;
  'last-message-at': string | null;
  version: number;
  'date-added': string;
  'date-updated': string;
  'group-source': string | null;
  'group-source-id': number | null;
  project?: ProjectInfo;
}

export interface ChannelListItemResponse extends ChannelDto {
  'read-only': boolean;
}

export interface ChannelMemberResponse {
  id: number;
  'channel-id': number;
  'user-id': number;
  role: ChannelMemberRoleEnum;
  'date-added': string;
  user?: UserInfo;
}

export interface WhatsappMemberResponse {
  id: number;
  phone: string;
  'display-name': string | null;
  status: string;
  'joined-at': string;
}

export interface ChannelPermissions {
  'can-send-message': boolean;
  'can-archive': boolean;
  'can-unarchive': boolean;
  'can-rename': boolean;
  'can-add-members': boolean;
  'can-remove-members': boolean;
  'can-leave': boolean;
  'can-manage-whatsapp': boolean;
}

export interface WhatsappResponse {
  status: string | null;
  'invite-link': string | null;
  'window-expires-at': string | null;
  members: WhatsappMemberResponse[];
}

export interface ChannelResponse extends ChannelDto {
  creator?: UserInfo;
  members: ChannelMemberResponse[];
  permissions?: ChannelPermissions;
  whatsapp?: WhatsappResponse;
}

export interface MessageAttachment {
  id: number;
  storedFileId: number;
  storedFile?: {
    id: number;
    name: string;
    accessKey: string;
    sizeInBytes: number;
    contentType: string;
    thumbnail?: {
      accessKey: string;
      width: number;
      height: number;
    };
  };
}

export interface ReplyToMessage {
  id: number;
  content: string | null;
  'sender-user-id': number | null;
  sender?: {
    id: number;
    'first-name': string;
    'last-name': string;
    avatar: string;
  };
}

export interface MessageResponse {
  id: number;
  'channel-id': number;
  'sender-user-id': number | null;
  type: MessageTypeEnum;
  content: string | null;
  'system-event': SystemEventEnum | null;
  'system-event-data': any | null;
  'reply-to-message-id': number | null;
  'is-edited': boolean;
  'is-deleted': boolean;
  'date-added': string;
  'date-updated': string;
  'delivery-status': 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  sender?: {
    id: number;
    'first-name': string;
    'last-name': string;
    avatar: string;
  };
  'whatsapp-sender-phone': string | null;
  'whatsapp-sender-name': string | null;
  attachments: MessageAttachment[];
  'reply-to-message': ReplyToMessage | null;
}

// Request body types
export interface CreateChannelBody {
  name: string;
  type: ChannelTypeEnum;
  'project-id'?: number;
  'member-user-ids'?: number[];
}

export interface SendMessageBody {
  content?: string;
  'reply-to-message-id'?: number;
  'stored-file-ids'?: number[];
}

export interface EditMessageBody {
  content: string;
}

export interface AddMembersBody {
  'user-ids': number[];
}

export interface MarkAsReadBody {
  'message-id': number;
}

export interface OpenDmBody {
  'target-user-id': number;
}

export interface RequestUploadBody {
  'file-name': string;
  'content-type': string;
  'size-in-bytes': number;
}

export interface ConfirmUploadBody {
  'stored-file-id': number;
}

// Response types
export interface ChannelsListResponse {
  rows: ChannelListItemResponse[];
  count: number;
}

export interface UnreadCountsResponse {
  [channelId: string]: number;
}

export interface EligibleUsersResponse {
  rows: UserInfo[];
  count: number;
}

export interface UploadUrlResponse {
  presignedUrl: string;
  storedFileId: number;
}

export interface ConfirmUploadResponse {
  storedFileId: number;
  accessKey: string;
  thumbnail: { accessKey: string; width: number; height: number } | null;
}
